import {
    deserializeTreeValue,
    isValueInOptionList,
    deserializeScope,
    getNodeIdWithKey,
} from 'components/inputs/TreeTablePicker/helpers';
import {
    BlockT,
    ScopeT,
    UserScopeT,
    SelectionTreeT,
} from 'business/definitions';
import {
    GESTIONNAIRE_TABLE,
    ORG_CHART_FINANCE_TABLE,
} from 'routes/BudgetModule/config/constants';
import { Value } from 'routes/HumanResourcesModule/helpers/types';

export const BUDGET_USER_RIGHTS_RESOURCES = [
    ORG_CHART_FINANCE_TABLE,
    GESTIONNAIRE_TABLE,
];

// These are the functions of the scopes that are added by default to every bind.
const DEFAULT_FUNCTIONS = ['hr', 'finance', 'population'];

export function getScopeBlocks(scope: ScopeT): BlockT[] {
    const exp = scope.expression;
    if (exp) {
        if (exp.any !== undefined) {
            return exp.any;
        }
        if (exp.all !== undefined) {
            return exp.all;
        }
        if (exp.block !== undefined) {
            // @ts-expect-error [TS migration] (previously $FlowFixMe) flow expects any and all structure in the returned type!
            return [exp];
        }
    }
    return [];
}

function getBlockValues(
    block: BlockT,
    scope: ScopeT,
    userScopes: UserScopeT[],
): string[] {
    const userScope = userScopes.find((uScope) => uScope.name === scope.name);
    // Case of user scope has blocks which are restricted (has blocks)
    if (userScope && 'blocks' in userScope) {
        const userBlock = userScope.blocks.find(
            (uscopeBlock) => uscopeBlock && uscopeBlock.name === block.block,
        );
        if (userBlock) {
            return userBlock.values;
        }
        return [];
    }
    return [];
}

// Make sure all values finish with :: and joint with |
function ensureEndsWithColon(word?: string | null): string | null | undefined {
    return word && word !== '' && !word.endsWith('::') ? `${word}::` : word;
}

const deserializeValues = (
    treeValues: TreeT | null,
    flatValues: Value[],
    values: string[],
): SelectionTreeT[] => {
    if (values) {
        if (treeValues !== null) {
            return deserializeTreeValue(
                values.map(ensureEndsWithColon).join('|'),
                treeValues,
                getNodeIdWithKey,
            );
        }
        return values.map((value) => {
            if (value && value.startsWith('scope:')) {
                return deserializeScope(value, []);
            }

            const flatValue = flatValues.find((val) => val?.value === value);

            if (flatValue) {
                return {
                    value,
                    label: flatValue.label,
                    isBadValue: !isValueInOptionList(value),
                    isSubtreeSelected: false,
                };
            }

            return {
                value,
                label: value,
                isBadValue: !isValueInOptionList(value),
                isSubtreeSelected: false,
            };
        });
    }
    return [];
};

export function getSelectedValuesOfTreeTablePicker(
    treeValues: TreeT | null,
    values: Value[],
    block: BlockT,
    scope: ScopeT,
    userScopes: UserScopeT[],
): SelectionTreeT[] {
    return deserializeValues(
        treeValues,
        values,
        getBlockValues(block, scope, userScopes),
    );
}

export const keepOnlyBoughtScopes = (
    scopes: ScopeT[],
    functions: Set<string>,
) => {
    // From the default functions, we select those who were not bought.
    const functionsToRemove = DEFAULT_FUNCTIONS.filter(
        (fun) => !functions.has(fun),
    ).map((fun) => `default_${fun}`);
    return scopes.filter((s) => !functionsToRemove.includes(s.name));
};

function allDescendants(node: TreeT, resultArr: string[]) {
    resultArr.push(node.value);
    node.children.forEach((c) => {
        allDescendants(c, resultArr);
    });
}

export function searchTree(node: TreeT, name: string, resultArr: string[]) {
    if (node) {
        if (node.value === name) {
            allDescendants(node, resultArr);
        } else if (node.children) {
            node.children.forEach((n) => {
                searchTree(n, name, resultArr);
            });
        }
    }
}
